import { css, keyframes } from '@emotion/react';
import { Box, Ellipsis, Flex, Image, Stack, Text, Tooltip } from '@inflearn/ds-react';
import { memo } from 'react';
import { MY_URL } from '../../../../constants/urls';
import { useVirtualScroll } from '../../../../hooks/useVirtualScroll';
import { CDN_BASE_PATH } from '../slot-dress-up/itemData';

// 상수
const SET_HEIGHT = 464;
const SET_OVERLAP = 80;
const CONTAINER_HEIGHT = 720;

const SET1_CARD_SLOTS = [
  { marginTop: 0 },
  { marginTop: 64 },
  { marginTop: 112 },
  { marginTop: 112 },
  { marginTop: 64 },
  { marginTop: 0 },
];

const SET2_CARD_SLOTS = [
  { marginTop: 20 },
  { marginTop: 84 },
  { marginTop: 112 },
  { marginTop: 84 },
  { marginTop: 20 },
];

const RANDOM_COMMENTS = [
  '메리 크리스마스!',
  '즐거운 연말 보내세요!',
  '행복한 연말 되세요!',
  '화이팅!',
];

const getRandomComment = (entryId: number): string => {
  return RANDOM_COMMENTS[entryId % RANDOM_COMMENTS.length];
};

// 타입
interface FeedEntry {
  id: number;
  userNickname: string;
  comment: string;
  imageUrl?: string;
}

export interface FeedSet {
  type: 'set1' | 'set2';
  items: Array<FeedEntry | null>;
}

interface PartyLookFeedProps {
  feedSets: FeedSet[];
}

// 카드 컴포넌트
const PolaroidCard = memo(function PolaroidCard({
  entry,
  marginTop,
  cardIndex,
}: {
  entry: FeedEntry;
  marginTop: number;
  cardIndex: number;
}) {
  const profileUrl = entry.id ? MY_URL.USER_PROFILE(entry.id) : undefined;
  const commentText = entry.comment || getRandomComment(entry.id);
  const isLongComment = commentText.length > 35;

  const cardContent = (
    <Box style={{ position: 'relative', marginTop }} css={cardSwingStyle(cardIndex)}>
      <Box css={clipStyle}>
        <Image src={`${CDN_BASE_PATH}/ch_ending_clip.png`} alt="클립" width={34} height="auto" />
      </Box>
      <Box css={cardStyle}>
        <Box css={imageContainerStyle}>
          <Image
            src={entry.imageUrl || `${CDN_BASE_PATH}/ch_bg_1.png`}
            alt={entry.userNickname || '캐릭터'}
            width="100%"
            height="auto"
            radius="md"
            style={{ display: 'block' }}
          />
        </Box>
        <Stack spacing={4} mt={12}>
          <Ellipsis lineClamp={1}>
            <Text fw={600} size="sm">
              {entry.userNickname || '익명'}
            </Text>
          </Ellipsis>
          <Tooltip
            label={commentText}
            multiline
            maw={170}
            withArrow
            position="bottom"
            disabled={!isLongComment}
            zIndex={9999}
            withinPortal
          >
            <div>
              <Ellipsis lineClamp={2}>
                <Text size="sm" c="gray.7" style={{ lineHeight: 1.5 }}>
                  {commentText}
                </Text>
              </Ellipsis>
            </div>
          </Tooltip>
        </Stack>
      </Box>
    </Box>
  );

  if (profileUrl) {
    return (
      <a href={profileUrl} target="_blank" rel="noopener noreferrer" css={linkStyle}>
        {cardContent}
      </a>
    );
  }
  return cardContent;
});

// 세트 컴포넌트
const FeedSetRow = memo(function FeedSetRow({
  set,
  setIndex,
  top,
}: {
  set: FeedSet;
  setIndex: number;
  top: number;
}) {
  const isSet1 = set.type === 'set1';
  const slots = isSet1 ? SET1_CARD_SLOTS : SET2_CARD_SLOTS;
  const stringImage = isSet1 ? 'ch_ending_string_1.png' : 'ch_ending_string_2.png';
  const gap = isSet1 ? 64 : 80;

  return (
    <Box
      style={{
        position: 'absolute',
        top,
        left: 0,
        right: 0,
        height: SET_HEIGHT,
      }}
    >
      <Box
        style={{
          position: 'absolute',
          top: 12,
          left: -20,
          right: -20,
          pointerEvents: 'none',
        }}
      >
        <Image
          src={`${CDN_BASE_PATH}/${stringImage}`}
          alt="폴라로이드 줄"
          width="100%"
          height="auto"
          style={{ display: 'block' }}
        />
      </Box>
      <Flex
        justify="center"
        align="flex-start"
        gap={gap}
        style={{ position: 'absolute', top: 72, left: 0, right: 0 }}
      >
        {slots.map((slot, slotIndex) => {
          const entry = set.items[slotIndex];
          if (!entry) {
            return (
              <Box
                key={`empty-${slotIndex}`}
                style={{
                  position: 'relative',
                  marginTop: slot.marginTop,
                  width: 170,
                  visibility: 'hidden',
                }}
              >
                <Box css={cardStyle} />
              </Box>
            );
          }
          return (
            <PolaroidCard
              key={entry.id}
              entry={entry}
              marginTop={slot.marginTop}
              cardIndex={setIndex * 6 + slotIndex}
            />
          );
        })}
      </Flex>
    </Box>
  );
});

// 메인 컴포넌트
export const PartyLookFeed = memo(function PartyLookFeed({ feedSets }: PartyLookFeedProps) {
  const { startIndex, endIndex, handleScroll } = useVirtualScroll({
    itemCount: feedSets.length,
    itemHeight: SET_HEIGHT - SET_OVERLAP,
    containerHeight: CONTAINER_HEIGHT,
  });

  const totalHeight =
    feedSets.length > 0 ? SET_HEIGHT + (feedSets.length - 1) * (SET_HEIGHT - SET_OVERLAP) + 80 : 0;

  return (
    <Box style={containerStyle} css={scrollHideStyle} onScroll={handleScroll}>
      <Box style={{ height: totalHeight, position: 'relative' }}>
        {Array.from({ length: Math.max(0, endIndex - startIndex + 1) }, (_, i) => {
          const setIndex = startIndex + i;
          const set = feedSets[setIndex];
          if (!set) return null;

          const top = setIndex * (SET_HEIGHT - SET_OVERLAP);
          return <FeedSetRow key={setIndex} set={set} setIndex={setIndex} top={top} />;
        })}
      </Box>
    </Box>
  );
});

// 스타일
const containerStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
};

const scrollHideStyle = css`
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const swingAnimation = keyframes`
  0%, 100% { transform: rotate(-0.8deg); }
  50% { transform: rotate(0.8deg); }
`;

const cardSwingStyle = (cardIndex: number) => css`
  animation: ${swingAnimation} ${2.5 + (cardIndex % 3) * 0.5}s ease-in-out infinite;
  animation-delay: ${(cardIndex % 5) * 0.2}s;
  transform-origin: top center;
`;

const cardStyle = css`
  background: white;
  border-radius: 12px;
  padding: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  width: 170px;
`;

const imageContainerStyle = css`
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  background: #f5f5f5;
`;

const clipStyle = css`
  position: absolute;
  top: -8px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
`;

const linkStyle = css`
  display: block;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
`;
